import { z } from 'zod';
import { RelationshipSchema } from '../relationship.schema';
import { EntitySchema } from './shared.schema';

export const OrganizationTitleSchema = z.object({
  id: z.string().describe('Stable ID for the organization title/rank'),
  name: z.string().min(1).describe('Display name of the title/rank'),
});

export const OrganizationMemberSchema = z.object({
  characterId: z.string().describe('ID of the character who belongs to this organization'),
  title: z.string().optional().describe('Optional title/rank assigned to this member'),
  titleId: z.string().optional().describe('Optional title/rank assigned to this member'),
});

export const OrganizationMemberGraphPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export const OrganizationMemberGraphThemeSchema = z.enum([
  'classic',
  'cyberpunk',
  'fantasy',
  'horror',
  'romantasy',
]);

/**
 * Organization schema.
 * Master source for Organization entity structure.
 */
export const OrganizationSchema = EntitySchema.extend({
  type: z.string().optional().describe('Type of Organization (e.g., guild, noble house, cult)'),
  parentOrganizationIds: z.array(z.string()).optional().describe('IDs of organizations this organization belongs to'),
  childOrganizationIds: z.array(z.string()).optional().describe('IDs of organizations that belong to this organization'),
  childOrganizationTitles: z.record(z.string(), z.string()).optional().describe('Title/rank assigned to child organizations in this organization'),
  organizationMemberGraphPositions: z.record(z.string(), OrganizationMemberGraphPositionSchema).optional().describe('Saved positions for this organization membership graph'),
  organizationMemberGraphTheme: OrganizationMemberGraphThemeSchema.optional().describe('Theme override for this organization membership graph'),
  organizationMemberGraphRelationships: z.array(RelationshipSchema).optional().describe('Relationships owned by this organization membership graph'),
  organizationTitles: z.array(OrganizationTitleSchema).optional(),
  organizationMembers: z.array(OrganizationMemberSchema).optional(),
});

export type Organization = z.infer<typeof OrganizationSchema>;
export type OrganizationTitle = z.infer<typeof OrganizationTitleSchema>;
export type OrganizationMember = z.infer<typeof OrganizationMemberSchema>;
